'use client'

import { fetchAddressByCep, formatCEP, unMaskCEP } from '@/lib/cep'
import { useState } from 'react'
import { MdSearch } from 'react-icons/md'

/**
 * Campos de um endereço. Espelha as colunas de `Address` no backend.
 *
 * `cityCode` é o código IBGE do município, preenchido pela consulta de CEP e
 * exigido pela emissão de NFS-e — por isso viaja junto mesmo sem aparecer na tela.
 */
export interface AddressFormValue {
    label?: string
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    cityCode?: string
    state?: string
    zipCode?: string
    country?: string
}

interface AddressFieldsProps {
    value: AddressFormValue
    onChange: (value: AddressFormValue) => void
    /**
     * Exibe o campo de título ("Unidade Centro"). Só faz sentido onde a entidade
     * pode ter mais de um endereço — é o que distingue as unidades na hora de
     * escolher o local do atendimento.
     */
    showLabel?: boolean
    disabled?: boolean
    /** Prefixo dos ids, para não colidir quando há vários blocos na mesma tela. */
    idPrefix?: string
}

/**
 * Bloco de campos de endereço com busca de CEP — usado no cadastro de parceiro,
 * profissional e hospital, e na confirmação de local do agendamento.
 *
 * Existe porque cada uma dessas telas reimplementava os oito campos e a própria
 * consulta de CEP, com duas bibliotecas concorrentes. Multiplicar isso por
 * "vários endereços por entidade" tornaria a divergência permanente.
 */
export default function AddressFields({
    value,
    onChange,
    showLabel = false,
    disabled = false,
    idPrefix = 'address',
}: AddressFieldsProps) {
    const [lookingUp, setLookingUp] = useState(false)
    const [notFound, setNotFound] = useState(false)

    const set = (patch: Partial<AddressFormValue>) => {
        onChange({ ...value, ...patch })
    }

    const handleCepChange = async (raw: string) => {
        const masked = formatCEP(raw)
        set({ zipCode: masked })
        setNotFound(false)

        if (unMaskCEP(masked).length !== 8) return

        setLookingUp(true)
        try {
            const found = await fetchAddressByCep(masked)
            if (!found) {
                setNotFound(true)
                return
            }
            // Só preenche o que veio: apagar o que o usuário já digitou (número,
            // complemento) por causa de uma consulta seria perda de trabalho.
            onChange({
                ...value,
                zipCode: masked,
                street: found.street ?? value.street,
                neighborhood: found.neighborhood ?? value.neighborhood,
                city: found.city ?? value.city,
                state: found.state ?? value.state,
                cityCode: found.ibge ?? value.cityCode,
            })
        } finally {
            setLookingUp(false)
        }
    }

    return (
        <div className="flex flex-col gap-4">
            {showLabel && (
                <div className="form-control w-full">
                    <label className="label pb-1" htmlFor={`${idPrefix}-label`}>
                        <span className="label-text font-medium">
                            Título do endereço
                        </span>
                    </label>
                    <input
                        id={`${idPrefix}-label`}
                        type="text"
                        className="input input-bordered w-full"
                        placeholder="Ex.: Unidade Centro, Laboratório Bairro Sul"
                        value={value.label || ''}
                        onChange={e => set({ label: e.target.value })}
                        disabled={disabled}
                    />
                    <label className="label pt-1">
                        <span className="label-text-alt text-base-content/60">
                            Aparece na escolha do local do atendimento
                        </span>
                    </label>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="form-control w-full">
                    <label className="label pb-1" htmlFor={`${idPrefix}-zip`}>
                        <span className="label-text font-medium">CEP</span>
                    </label>
                    <div className="relative">
                        <input
                            id={`${idPrefix}-zip`}
                            type="text"
                            inputMode="numeric"
                            className="input input-bordered w-full pr-10"
                            placeholder="00000-000"
                            value={value.zipCode || ''}
                            onChange={e => handleCepChange(e.target.value)}
                            disabled={disabled}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/40">
                            {lookingUp ? (
                                <span className="loading loading-spinner loading-xs" />
                            ) : (
                                <MdSearch size={18} />
                            )}
                        </span>
                    </div>
                    {notFound && (
                        <label className="label pt-1">
                            <span className="label-text-alt text-warning">
                                CEP não encontrado — preencha manualmente
                            </span>
                        </label>
                    )}
                </div>

                <div className="form-control w-full sm:col-span-2">
                    <label className="label pb-1" htmlFor={`${idPrefix}-street`}>
                        <span className="label-text font-medium">Logradouro</span>
                    </label>
                    <input
                        id={`${idPrefix}-street`}
                        type="text"
                        className="input input-bordered w-full"
                        placeholder="Rua, avenida..."
                        value={value.street || ''}
                        onChange={e => set({ street: e.target.value })}
                        disabled={disabled}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="form-control w-full">
                    <label className="label pb-1" htmlFor={`${idPrefix}-number`}>
                        <span className="label-text font-medium">Número</span>
                    </label>
                    <input
                        id={`${idPrefix}-number`}
                        type="text"
                        className="input input-bordered w-full"
                        value={value.number || ''}
                        onChange={e => set({ number: e.target.value })}
                        disabled={disabled}
                    />
                </div>

                <div className="form-control w-full sm:col-span-2">
                    <label
                        className="label pb-1"
                        htmlFor={`${idPrefix}-complement`}
                    >
                        <span className="label-text font-medium">
                            Complemento
                        </span>
                    </label>
                    <input
                        id={`${idPrefix}-complement`}
                        type="text"
                        className="input input-bordered w-full"
                        placeholder="Sala, andar, bloco..."
                        value={value.complement || ''}
                        onChange={e => set({ complement: e.target.value })}
                        disabled={disabled}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="form-control w-full sm:col-span-2">
                    <label
                        className="label pb-1"
                        htmlFor={`${idPrefix}-neighborhood`}
                    >
                        <span className="label-text font-medium">Bairro</span>
                    </label>
                    <input
                        id={`${idPrefix}-neighborhood`}
                        type="text"
                        className="input input-bordered w-full"
                        value={value.neighborhood || ''}
                        onChange={e => set({ neighborhood: e.target.value })}
                        disabled={disabled}
                    />
                </div>

                <div className="form-control w-full">
                    <label className="label pb-1" htmlFor={`${idPrefix}-city`}>
                        <span className="label-text font-medium">Cidade</span>
                    </label>
                    <input
                        id={`${idPrefix}-city`}
                        type="text"
                        className="input input-bordered w-full"
                        value={value.city || ''}
                        onChange={e => set({ city: e.target.value })}
                        disabled={disabled}
                    />
                </div>

                <div className="form-control w-full">
                    <label className="label pb-1" htmlFor={`${idPrefix}-state`}>
                        <span className="label-text font-medium">UF</span>
                    </label>
                    <input
                        id={`${idPrefix}-state`}
                        type="text"
                        maxLength={2}
                        className="input input-bordered w-full uppercase"
                        value={value.state || ''}
                        onChange={e =>
                            set({ state: e.target.value.toUpperCase() })
                        }
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>
    )
}

/** Resumo em uma linha, para listas e para o seletor do agendamento. */
export function formatAddressLine(address: AddressFormValue): string {
    const street = [address.street, address.number]
        .filter(Boolean)
        .join(', ')
    return [street, address.complement, address.neighborhood, address.city]
        .filter(part => !!part && `${part}`.trim())
        .join(' - ')
}
